import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Button,
    Typography
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { SitePhotoDto, PHOTO_CATEGORIES, PHOTO_CATEGORY_LABELS, PhotoCategory, UpdateSitePhotoRequest } from "../../../../../../../dtos/sitePhoto";

const useStyles = makeStyles((theme: Theme) => ({
    formField: {
        margin: "16px 0",
        width: "100%",
    },
    loadingContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "200px",
        color: "#999",
    },
    descriptionField: {
        "& .MuiInputBase-root": {
            minHeight: "80px",
            resize: "vertical",
        },
        "& .MuiInputBase-input": {
            minHeight: "60px",
            resize: "vertical",
        }
    }
}));

interface EditPhotoDialogProps {
    open: boolean;
    photo: SitePhotoDto | null;
    onClose: () => void;
    onSave: (photoId: number, updateData: UpdateSitePhotoRequest) => Promise<boolean>;
}

export const EditPhotoDialog: React.FC<EditPhotoDialogProps> = ({
    open,
    photo,
    onClose,
    onSave
}) => {
    const classes = useStyles();
    const [editDescription, setEditDescription] = useState("");
    const [editCategory, setEditCategory] = useState<PhotoCategory>("uncategorized");
    const [validationError, setValidationError] = useState<string | null>(null);
    const MAX_DESCRIPTION_LENGTH = 500;
    const getCharacterCount = (value: string): number => {
        const anyIntl: any = Intl as any;
        if (typeof anyIntl !== "undefined" && anyIntl.Segmenter) {
            const segmenter = new anyIntl.Segmenter(undefined, { granularity: "grapheme" });
            return Array.from(segmenter.segment(value)).length;
        }
        return Array.from(value).length;
    };



    useEffect(() => {
        if (photo) {
            setEditDescription(photo.description || "");
            setEditCategory((photo.category as PhotoCategory) || "uncategorized");
            setValidationError(null);
        }
    }, [photo]);

    const handleSave = async () => {
        if (!photo) return;

        const count = getCharacterCount(editDescription);
        if (count > MAX_DESCRIPTION_LENGTH) {
            setValidationError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
            return;
        }
        setValidationError(null);

        const updateData: UpdateSitePhotoRequest = {
            description: editDescription.trim() || undefined,
            category: editCategory,
        };

        const success = await onSave(photo.id, updateData);
        if (success) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            {photo ? (
                <>
                    <DialogTitle>Edit Photo</DialogTitle>
                    <DialogContent>
                        <FormControl className={classes.formField}>
                            <Typography variant="body2" gutterBottom>Category</Typography>
                            <Select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value as PhotoCategory)}
                                variant="outlined"
                            >
                                {PHOTO_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {PHOTO_CATEGORY_LABELS[cat]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl className={classes.formField}>
                            <Typography variant="body2" gutterBottom>Description</Typography>
                            <TextField
                                value={editDescription}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEditDescription(value);
                                    const count = getCharacterCount(value);
                                    if (count > MAX_DESCRIPTION_LENGTH) {
                                        setValidationError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
                                    } else if (validationError) {
                                        setValidationError(null);
                                    }
                                }}
                                multiline
                                rows={3}
                                variant="outlined"
                                placeholder="Describe what this photo shows..."
                                error={!!validationError}
                                helperText={validationError || `${getCharacterCount(editDescription)}/${MAX_DESCRIPTION_LENGTH} characters`}
                                className={classes.descriptionField}
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} color="primary" variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <DialogContent>
                    <div className={classes.loadingContainer}>
                        Loading...
                    </div>
                </DialogContent>
            )}
        </Dialog>
    );
}; 